import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, IdentityRecord, RbacConfig, RolePreview } from '../api/client'
import { fmtDateTime } from '../utils/datetime'
import { theme } from '../theme'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'
import { enforcementLock } from './identityEnforcement'

/**
 * Identity & role mapping.
 *
 * Observation phase: the mapping can be configured, previewed and saved here,
 * but NOTHING is enforced yet — every authenticated user still has full access.
 * Enforcement lands in a later release once the mapping is confirmed.
 */
export default function IdentitySettings() {
  const isAdmin = useIsAdmin()
  const [identities, setIdentities] = useState<IdentityRecord[]>([])
  const [loading, setLoading] = useState(true)

  const [paths, setPaths] = useState<string[]>([])
  const [claimPath, setClaimPath] = useState('')
  const [values, setValues] = useState<string[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [valueSearch, setValueSearch] = useState('')

  const [saved, setSaved] = useState<RbacConfig | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const [preview, setPreview] = useState<RolePreview | null>(null)
  const [previewing, setPreviewing] = useState(false)

  useEffect(() => {
    Promise.all([
      api.listIdentities().catch(() => []),
      api.getClaimPaths().catch(() => []),
      api.getRbacConfig().catch(() => null),
    ])
      .then(([ids, p, cfg]) => {
        setIdentities(ids)
        setPaths(p)
        setSaved(cfg)
        if (cfg?.role_claim_path) {
          setClaimPath(cfg.role_claim_path)
          setSelected(cfg.admin_values || [])
        }
      })
      .finally(() => setLoading(false))
  }, [])

  // Load the observed values whenever the chosen path changes — picking from
  // real values avoids a typo that would silently match nobody.
  const loadValues = useCallback((path: string) => {
    if (!path) { setValues([]); return }
    api.getClaimValues(path).then(setValues).catch(() => setValues([]))
  }, [])

  useEffect(() => { loadValues(claimPath) }, [claimPath, loadValues])

  const filteredValues = useMemo(() => {
    const q = valueSearch.trim().toLowerCase()
    return q ? values.filter(v => v.toLowerCase().includes(q)) : values
  }, [values, valueSearch])

  const toggleValue = (v: string) =>
    setSelected(s => (s.includes(v) ? s.filter(x => x !== v) : [...s, v]))

  const runPreview = () => {
    setPreviewing(true)
    api.previewRole({ claim: claimPath, admin_values: selected })
      .then(setPreview)
      .catch((e: any) => window.alert(e.body?.detail || e.message || 'Preview failed'))
      .finally(() => setPreviewing(false))
  }

  const save = (enforcement?: boolean) => {
    setSaving(true)
    setSaveError(null)
    api.putRbacConfig({
      role_claim_path: claimPath,
      admin_values: selected,
      ...(enforcement === undefined ? {} : { enforcement_enabled: enforcement }),
    })
      .then(cfg => { setSaved(cfg); setSaveError(null) })
      .catch((e: any) => setSaveError(e.body?.detail || e.message || 'Save failed'))
      .finally(() => setSaving(false))
  }

  const dirty =
    claimPath !== (saved?.role_claim_path || '') ||
    JSON.stringify([...selected].sort()) !== JSON.stringify([...(saved?.admin_values || [])].sort())

  // THE DEPLOY OVERRIDE MAKES THIS TOGGLE INERT (FINISH-LINE A7.18 / E2).
  //
  // `is_enforcement_active` consults ROLE_ENFORCEMENT before it loads the config row, so
  // while that variable is set the toggle cannot move enforcement. Read from the DECLARED
  // `enforcement_override` field, never from the wording of a `warnings` entry: this line
  // used to be `warnings.some(w => w.includes('ROLE_ENFORCEMENT'))`, which tied a control's
  // disabled state to a sentence's phrasing, and the sentence has since been removed.
  //
  // `lock` carries the disabled flag AND its label as one value, so the control cannot end
  // up disabled-and-unlabelled — see `identityEnforcement.ts`.
  const lock = enforcementLock(saved?.enforcement_override)

  if (loading) return <div>Loading identities…</div>
  if (!isAdmin) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Identity &amp; Role Mapping</h2>
        <AdminOnly what="Identity and role mapping" />
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Identity &amp; Role Mapping</h2>

      {saved?.enforcement_active ? (
        <div style={{ ...card, borderColor: '#4caf50', marginBottom: '1.5rem' }}>
          <strong style={{ color: '#4caf50' }}>Enforcement is ACTIVE.</strong>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: theme.textMuted, lineHeight: 1.6 }}>
            Users holding any one of the selected values have the admin role; everyone else is
            read-only. A user's role resolves from their own claims at their own login — colleagues
            in an admin role do <strong>not</strong> need to have logged in beforehand.
          </p>
        </div>
      ) : (
        <div style={{ ...card, borderColor: theme.primary, marginBottom: '1.5rem' }}>
          <strong>Enforcement is OFF — every user currently has full access.</strong>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: theme.textMuted, lineHeight: 1.6 }}>
            Configure and preview the mapping below, then enable enforcement. Enabling is refused
            if the mapping resolves to zero admins and no break-glass override is set, so it cannot
            lock you out. Decoded claims only are stored — never the ID, access or refresh token.
          </p>
        </div>
      )}

      {saved?.warnings && saved.warnings.length > 0 && (
        <ul style={{ margin: '0 0 1.25rem', paddingLeft: '1.1rem', fontSize: '0.82rem', color: '#ffa726', lineHeight: 1.6 }}>
          {saved.warnings.map((w, n) => <li key={n}>{w}</li>)}
        </ul>
      )}

      {/* ---- Mapping configuration ---- */}
      <h3>Admin role mapping</h3>
      <div style={card}>
        <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '1rem' }}>
          <div style={{ color: theme.textMuted, marginBottom: 4 }}>
            Claim path — dotted for nested claims (e.g. <code>params.roles</code>)
          </div>
          <select
            value={claimPath}
            onChange={e => { setClaimPath(e.target.value); setSelected([]); setValueSearch('') }}
            style={{ ...input, minWidth: 280 }}
          >
            <option value="">— select a claim —</option>
            {paths.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <span style={{ marginLeft: '0.75rem', fontSize: '0.75rem', color: theme.textMuted }}>
            {paths.length} claim{paths.length === 1 ? '' : 's'} observed from your IdP
          </span>
        </label>

        {claimPath && (
          <>
            <div style={{ color: theme.textMuted, fontSize: '0.8rem', marginBottom: 6 }}>
              Values that grant admin — {selected.length} selected of {values.length} observed
            </div>
            <input
              value={valueSearch}
              onChange={e => setValueSearch(e.target.value)}
              placeholder={`Search ${values.length} values…`}
              style={{ ...input, width: '100%', marginBottom: '0.5rem' }}
            />
            <div style={{
              maxHeight: 260, overflowY: 'auto', border: `1px solid ${theme.border}`,
              borderRadius: 4, padding: '0.5rem', background: theme.bg,
            }}>
              {filteredValues.length === 0 && (
                <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                  {values.length === 0 ? 'No values observed at this claim path.' : 'No matches.'}
                </span>
              )}
              {filteredValues.map(v => (
                <label key={v} style={{
                  display: 'flex', alignItems: 'center', gap: '0.45rem',
                  padding: '0.15rem 0', fontSize: '0.8rem', cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={selected.includes(v)} onChange={() => toggleValue(v)} />
                  <span>{v}</span>
                </label>
              ))}
            </div>

            {selected.length > 0 && (
              <div style={{ marginTop: '0.6rem', fontSize: '0.78rem' }}>
                <span style={{ color: theme.textMuted }}>Selected: </span>
                {selected.map(v => (
                  <span key={v} style={chip} onClick={() => toggleValue(v)} title="Click to remove">
                    {v} ×
                  </span>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
              <button onClick={runPreview} disabled={previewing || !claimPath} style={btnPrimary}>
                {previewing ? 'Evaluating…' : 'Preview'}
              </button>
              <button onClick={() => save()} disabled={saving || !claimPath || !dirty} style={btn}>
                {saving ? 'Saving…' : dirty ? 'Save mapping' : 'Saved'}
              </button>
              {saved?.updated_at && (
                <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>
                  Last saved {fmtDateTime(saved.updated_at)}
                  {saved.updated_by ? ` by ${saved.updated_by}` : ''}
                </span>
              )}
            </div>

            <div style={{
              marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.border}`,
              display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap',
            }}>
              <button
                onClick={() => save(!saved?.enforcement_enabled)}
                disabled={saving || !claimPath || dirty || lock !== null}
                title={
                  lock
                    ? lock.label
                    : dirty ? 'Save the mapping first' : undefined
                }
                style={
                  // Greyed out whenever the button cannot act, so it reads as
                  // unavailable rather than as a live control that does nothing.
                  (saving || !claimPath || dirty || lock !== null)
                    ? btnDisabled
                    : saved?.enforcement_enabled
                      ? { ...btn, borderColor: '#ef5350', color: '#ef5350' }
                      : { ...btnPrimary, background: '#4caf50', borderColor: '#4caf50' }
                }
              >
                {saved?.enforcement_enabled ? 'Disable enforcement' : 'Enable enforcement'}
              </button>
              {/* THE LABEL IS NOT OPTIONAL (A7.18 / E2). It renders from the same `lock`
                  value that disables the button above, so there is no state in which the
                  control is dead and silent. It replaces a two-sentence paragraph; the
                  full precedence rule lives in `identityEnforcement.ts` and in
                  backend/api/identity.py beside the override that causes it. */}
              {lock && (
                <span style={{ fontSize: '0.78rem', color: '#ffa726' }}>{lock.label}</span>
              )}
              <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                {saved?.resolvable_admins ?? 0} of {identities.length} recorded identit
                {identities.length === 1 ? 'y' : 'ies'} would be admin
                {dirty ? ' · save the mapping before toggling' : ''}
              </span>
            </div>

            {saveError && (
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.8rem', color: '#ef5350' }}>{saveError}</p>
            )}
          </>
        )}
      </div>

      {/* ---- Preview ---- */}
      {preview && (
        <div style={{ ...card, marginTop: '1rem' }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
            <Stat label="Identities" value={String(preview.total_identities)} />
            <Stat label="Would be admin" value={String(preview.would_be_admin_count)} />
            <Stat
              label="You would be admin"
              value={preview.you_would_be_admin == null ? '—' : preview.you_would_be_admin ? 'Yes' : 'No'}
              color={preview.you_would_be_admin === false ? '#ef5350' : preview.you_would_be_admin ? '#4caf50' : undefined}
            />
          </div>

          {preview.warnings.length > 0 && (
            <ul style={{ margin: '0 0 0.85rem', paddingLeft: '1.1rem', fontSize: '0.82rem', color: '#ffa726', lineHeight: 1.6 }}>
              {preview.warnings.map((w, n) => <li key={n}>{w}</li>)}
            </ul>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: theme.bgElevated, textAlign: 'left' }}>
                <th style={th}>User</th>
                <th style={th}>Matched</th>
                <th style={th}>Values at claim</th>
                <th style={th}>Result</th>
              </tr>
            </thead>
            <tbody>
              {preview.entries.map(e => (
                <tr key={e.subject} style={{ borderTop: `1px solid ${theme.border}` }}>
                  <td style={td}>
                    {e.name || e.email || e.subject}
                    {e.is_you && <span style={youTag}>YOU</span>}
                  </td>
                  <td style={td}>
                    {e.matched_values.length
                      ? e.matched_values.join(', ')
                      : <span style={{ color: theme.textMuted }}>{e.claim_present ? 'none' : 'claim absent'}</span>}
                  </td>
                  <td style={{ ...td, color: theme.textMuted }}>{e.value_count}</td>
                  <td style={td}>
                    {e.would_be_admin
                      ? <span style={{ color: '#4caf50', fontWeight: 600 }}>admin</span>
                      : <span style={{ color: theme.textMuted }}>viewer</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Recorded identities ---- */}
      <h3 style={{ marginTop: '2rem' }}>Recorded identities ({identities.length})</h3>
      {identities.length === 0 ? (
        <div style={card}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: theme.textMuted }}>
            No identities recorded yet. Claims are captured on login — sign out and back in, and
            have the users who should be admins log in too.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: theme.bgAlt, borderRadius: 8 }}>
          <thead>
            <tr style={{ background: theme.bgElevated, textAlign: 'left' }}>
              <th style={th}>User</th>
              <th style={th}>Subject</th>
              <th style={th}>Logins</th>
              <th style={th}>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {identities.map(i => (
              <tr key={i.subject} style={{ borderTop: `1px solid ${theme.border}` }}>
                <td style={td}>
                  {i.name || i.preferred_username || i.email || '—'}
                  {i.is_you && <span style={youTag}>YOU</span>}
                  {i.email && <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{i.email}</div>}
                </td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' }}>{i.subject}</td>
                <td style={td}>{i.login_count}</td>
                <td style={{ ...td, fontSize: '0.78rem' }}>{fmtDateTime(i.last_seen_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: color || theme.text }}>{value}</div>
    </div>
  )
}

const card: React.CSSProperties = {
  background: theme.bgAlt, border: `1px solid ${theme.border}`, borderRadius: 8, padding: '1rem',
}
const th: React.CSSProperties = { padding: '0.5rem 0.75rem', fontSize: '0.78rem', color: theme.textMuted }
const td: React.CSSProperties = { padding: '0.5rem 0.75rem' }
const btn: React.CSSProperties = {
  padding: '0.35rem 0.75rem', fontSize: '0.78rem', background: theme.bgElevated,
  color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4, cursor: 'pointer',
}
const btnPrimary: React.CSSProperties = {
  ...btn, background: theme.primary, color: '#fff', borderColor: theme.primary, fontWeight: 600,
}
/** Unmistakably inactive: muted fill, muted text, not-allowed cursor. */
const btnDisabled: React.CSSProperties = {
  ...btn,
  background: theme.bg,
  color: theme.textMuted,
  borderColor: theme.border,
  cursor: 'not-allowed',
  opacity: 0.6,
}
const input: React.CSSProperties = {
  padding: '0.45rem 0.5rem', fontSize: '0.85rem', background: theme.bg,
  color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4, boxSizing: 'border-box',
}
const chip: React.CSSProperties = {
  display: 'inline-block', marginRight: 6, marginBottom: 4, padding: '0.1rem 0.45rem',
  background: theme.bgElevated, border: `1px solid ${theme.border}`, borderRadius: 12, cursor: 'pointer',
}
const youTag: React.CSSProperties = {
  marginLeft: 6, fontSize: '0.7rem', color: theme.primary, fontWeight: 700,
}
